
import { apiGet, apiPost, apiPut, apiDelete } from './apiUtils';

const userService = {
  /**
   * Get all users (for admin management)
   * @returns {Promise<Object>} List of all users
   */
  getAllUsers: async () => {
    try {
      console.log('🔍 Fetching All Users');
      console.log('Auth Token:', localStorage.getItem('authToken'));

      // Try multiple possible endpoints for getting all users
      const endpoints = [
        '/admin/users',
        '/users/all',
        '/api/admin/users',
        '/api/users/all'
      ];

      let response = null;
      let lastError = null;

      for (const endpoint of endpoints) {
        try {
          console.log(`🔍 Trying endpoint: ${endpoint}`);
          response = await apiGet(endpoint);
          console.log('✅ All Users Response:', response);

          if (response && (response.success || response.data)) {
            const users = response.data?.users || response.data || [];
            const normalizedUsers = Array.isArray(users) ? users.map(user => ({
              ...user,
              name: user.first_name && user.last_name
                ? `${user.first_name} ${user.last_name}`.trim()
                : user.first_name || user.last_name || user.email || 'No Name',
            })) : [];

            return {
              success: true,
              data: normalizedUsers
            };
          }
        } catch (err) {
          console.warn(`❌ Endpoint ${endpoint} failed:`, err.message);
          lastError = err;
          continue;
        }
      }

      // If all endpoints failed, throw the last error
      throw lastError || new Error('All user endpoints failed');
    } catch (error) {
      console.error('Error fetching all users:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch users',
        data: []
      };
    }
  },

  /**
   * Get list of sub-users
   * @returns {Promise<Object>} List of sub-users
   */
  getSubUsers: async () => {
    try {
      console.log('🔍 Fetching Sub Users');
      console.log('Auth Token:', localStorage.getItem('authToken'));
      const response = await apiGet('/users/sub-users', {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      console.log('Sub Users Response:', response);

      // Normalize user data
      const users = response.data.map(user => ({
        ...user,
        name: user.first_name && user.last_name
          ? `${user.first_name} ${user.last_name}`.trim()
          : user.first_name || user.last_name || user.email || 'No Name',
      }));

      return {
        success: true,
        data: users
      };
    } catch (error) {
      console.error('Error fetching sub-users:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch users'
      };
    }
  },

  /**
   * Get user chain (team members) - EXACT mobile app functionality
   * This matches userService.getUserChain() from the mobile app (lines 88-90)
   * Returns only users in the current user's hierarchy chain.
   * Parent: all, City Manager: chain, Manager: self+sub-users, Sub-User: self
   * @returns {Promise<Object>} List of team members (chain-filtered based on role)
   */
  getUserChain: async () => {
    try {
      console.log('🔍 Fetching User Chain (Team Members) - Hierarchy Filtered');
      console.log('Auth Token:', localStorage.getItem('authToken'));

      // Use the exact same endpoint as mobile app for chain-filtered users
      const response = await apiGet('/api/users/sub-users/chain', {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      console.log('User Chain Response:', response);

      // Normalize user data exactly like mobile app
      const users = response.data.map(user => ({
        ...user,
        name: user.first_name && user.last_name
          ? `${user.first_name} ${user.last_name}`.trim()
          : user.first_name || user.last_name || user.email || 'No Name',
        // Ensure role and user_type are both available for compatibility
        role: user.role || user.user_type,
        user_type: user.user_type || user.role
      }));

      console.log('✅ User Chain (Team Members) loaded - Hierarchy Filtered:', users.length);

      return {
        success: true,
        data: users
      };
    } catch (error) {
      console.error('Error fetching user chain:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch user chain',
        data: []
      };
    }
  },

  /**
   * Create a new sub-user
   * @param {Object} userData - User data to create
   * @returns {Promise<Object>} Create user response
   */
  getBuilderPublicInfo: async (builderId) => {
    try {
      return await apiGet(`/api/public/builder/${builderId}`);
    } catch (error) {
      console.error('Error fetching builder public info:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch builder info'
      };
    }
  },
  getBuilderReviewsById: async (builderId) => {
    try {
      return await apiGet(`/api/public/builder/${builderId}/reviews`);
    } catch (error) {
      console.error('Error fetching builder reviews:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch builder reviews'
      };
    }
  },
  createSubUser: async (userData) => {
    try {
      // Convert field names to match backend expectations
      const nameParts = userData.name?.trim().split(' ') || [];
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Convert user type to backend format
      const userTypeMap = {
        'City Manager': 'city_manager',
        'Manager': 'manager',
        'Sub User': 'sub_user',
      };
      const role = userTypeMap[userData.userType] || userData.userType?.toLowerCase().replace(/ /g, '_') || 'manager';

      const payload = {
        first_name: firstName,
        last_name: lastName,
        email: userData.email,
        phone: userData.phone,
        password: userData.password,
        user_type: role,
        city: userData.city || '',
        role: role,
        assigned_users: userData.assignedUsers || []
      };

      console.log('Creating sub-user with payload:', JSON.stringify(payload, null, 2));
      return await apiPost('/users/sub-users', payload);
    } catch (error) {
      console.error('Error creating sub-user:', error.response?.data || error.message);
      // If backend fails, provide a more detailed error message
      if (error.response?.status === 500) {
        return {
          success: false,
          message: error.response?.data?.message || 'Server error. Please check all fields and try again.'
        };
      }
      throw error;
    }
  },

  /**
   * Update an existing sub-user
   * @param {string} userId - ID of the user to update
   * @param {Object} updateData - Updated user data
   * @returns {Promise<Object>} Update user response
   */
  updateSubUser: async (userId, userData) => {
    // Convert field names to match backend expectations
    const payload = {
      first_name: userData.name?.split(' ')[0] || userData.name,
      last_name: userData.name?.split(' ')[1] || '',
      email: userData.email,
      phone: userData.phone,
      city: userData.city,
      role: userData.userType?.toLowerCase().replace(' ', '_'),
      assigned_users: userData.assignedUsers || []
    };

    // Only include password if provided
    if (userData.password) {
      payload.password = userData.password;
    }

    return await apiPut(`/users/sub-users/${userId}`, payload);
  },

  /**
   * Delete a sub-user
   * @param {string} userId - ID of the user to delete
   * @returns {Promise<Object>} Delete user response
   */
  deleteSubUser: async (userId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await apiDelete(`/api/users/sub-users/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      return {
        success: true,
        data: response.data,
        message: 'User deleted successfully'
      };
    } catch (error) {
      console.error('Error deleting sub-user:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete user'
      };
    }
  },

  /**
   * Get all achievements for current user
   * @returns {Promise<Object>} - List of achievements
   */
  getAchievements: async () => {
    try {
      const response = await apiGet('/api/auth/achievements');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error fetching achievements:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch achievements'
      };
    }
  },

  /**
   * Add a new achievement
   * @param {Object} achievementData - Achievement data (title, image_url)
   * @returns {Promise<Object>} - Created achievement
   */
  addAchievement: async (achievementData) => {
    try {
      const payload = {
        title: achievementData.title,
        image_url: achievementData.image_url || null
      };

      const response = await apiPost('/api/auth/achievements', payload);

      return {
        success: true,
        data: response.data,
        message: 'Achievement added successfully'
      };
    } catch (error) {
      console.error('Error adding achievement:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to add achievement'
      };
    }
  },

  /**
   * Update an existing achievement
   * @param {string} achievementId - Achievement ID
   * @param {Object} achievementData - Updated achievement data
   * @returns {Promise<Object>} - Updated achievement
   */
  updateAchievement: async (achievementId, achievementData) => {
    try {
      const payload = {
        title: achievementData.title,
        image_url: achievementData.image_url || null
      };

      const response = await apiPut(`/api/auth/achievements/${achievementId}`, payload);

      return {
        success: true,
        data: response.data,
        message: 'Achievement updated successfully'
      };
    } catch (error) {
      console.error('Error updating achievement:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update achievement'
      };
    }
  },

  /**
   * Delete an achievement
   * @param {string} achievementId - Achievement ID
   * @returns {Promise<Object>} - Delete confirmation
   */
  deleteAchievement: async (achievementId) => {
    try {
      const response = await apiDelete(`/api/auth/achievements/${achievementId}`);

      return {
        success: true,
        data: response.data,
        message: 'Achievement deleted successfully'
      };
    } catch (error) {
      console.error('Error deleting achievement:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete achievement'
      };
    }
  },

  /**
   * Get builder partnerships for the current agent
   * @returns {Promise<Object>} - List of builder partnerships
   */
  getBuilderPartnerships: async () => {
    try {
      // Try multiple possible endpoints
      const endpoints = [
        '/api/partnerships',  // Simplified endpoint first
        '/api/crm/builder/partnerships',
        '/api/auth/partnerships',
        '/api/public/agent/partnerships',
        '/api/builder/partnerships',
        '/partnerships'  // Last resort with no prefix
      ];

      for (const endpoint of endpoints) {
        try {
          const response = await apiGet(endpoint);
          if (response && response.data) {
            console.log(`Successful partnerships fetch from ${endpoint}`);
            return {
              success: true,
              data: response.data
            };
          }
        } catch (err) {
          console.warn(`Endpoint ${endpoint} failed: ${err.message}`);
        }
      }

      // If no endpoint works, return last known partnerships or empty response
      console.error('No valid partnerships endpoint found');
      return {
        success: false,
        data: [],
        message: 'Unable to fetch partnerships. Please check your network connection.'
      };
    } catch (error) {
      console.error('Critical error fetching builder partnerships:', error);
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || 'Failed to fetch builder partnerships'
      };
    }
  },

  /**
   * Get builder partner details
   * @param {string} builderId - ID of the builder
   * @returns {Promise<Object>} - Builder partner details
   */
  getBuilderPartnerDetails: async (builderId) => {
    try {
      const response = await apiGet(`/api/crm/builder/${builderId}`);
      return {
        success: true,
        data: response.data || {}
      };
    } catch (error) {
      console.error('Error fetching builder partner details:', error);
      return {
        success: false,
        data: {},
        message: error.response?.data?.message || 'Failed to fetch builder partner details'
      };
    }
  },

  /**
   * Get agent leads for a specific builder
   * @param {string} builderId - ID of the builder
   * @returns {Promise<Object>} - List of leads
   */
  getAgentLeads: async (builderId) => {
    try {
      const response = await apiGet(`/api/crm/builder/${builderId}/leads`);
      return {
        success: true,
        data: response.data || []
      };
    } catch (error) {
      console.error('Error fetching agent leads:', error);
      return {
        success: false,
        data: [],
        message: error.response?.data?.message || 'Failed to fetch agent leads'
      };
    }
  },

  /**
   * Get conversations (Admin Chat)
   * @param {Object} filters - { type, status }
   * @param {Object} pagination - { page, limit }
   * @returns {Promise<Object>} - List of conversations
   */
  getConversations: async (filters = {}, pagination = {}) => {
    try {
      console.log('🔍 Fetching conversations with filters:', filters, 'pagination:', pagination);

      const response = await apiPost('/api/admin/chats/conversations', {
        filter: filters,
        pagination
      });

      console.log('✅ Conversations Response:', response);
      return response;
    } catch (error) {
      console.error('Error loading conversations:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to load conversations',
        data: []
      };
    }
  },

  /**
   * Get support messages/conversation history
   * @param {Object} data - { conversation_id, pagination }
   * @returns {Promise<Object>} - List of support messages
   */
  getSupportMessages: async (data = {}) => {
    try {
      console.log('🔍 Fetching support messages:', data);

      const response = await apiPost('/api/admin/chats/messages', data);

      console.log('✅ Messages Response:', response);
      return response;
    } catch (error) {
      console.error('Error loading messages:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to load messages',
        data: []
      };
    }
  },

  /**
   * Send a message to support
   * @param {Object} data - Support message data
   * @returns {Promise<Object>} - Success response
   */
  contactSupport: async (data) => {
    try {
      console.log('🔍 Sending support message:', data);

      const response = await apiPost('/api/admin/chats/send', data);

      console.log('✅ Send Message Response:', response);
      return response;
    } catch (error) {
      console.error('Error sending message:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to send message'
      };
    }
  },

  /**
   * Upload file for chat
   * @param {FormData} formData - File data
   * @returns {Promise<Object>} - File URL
   */
  uploadChatFile: async (formData) => {
    try {
      console.log('🔍 Uploading chat file');

      const response = await apiPost('/api/admin/chats/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      console.log('✅ Upload Response:', response);
      return response;
    } catch (error) {
      console.error('Error uploading file:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to upload file'
      };
    }
  },

  /**
   * Mark chat messages as read (matches React Native app)
   * @param {string} conversationId - Conversation ID
   * @param {string} userId - User ID who is marking as read
   * @returns {Promise<Object>} - Mark as read response
   */
  markAsRead: async (conversationId, userId) => {
    try {
      console.log('🔍 Marking messages as read for conversation:', conversationId, 'user:', userId);

      const response = await apiPost('/api/admin/chats/read', {
        conversation_id: conversationId,
        user_id: userId
      });

      console.log('✅ Mark as read Response:', response);
      return response;
    } catch (error) {
      console.error('Error marking messages as read:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to mark messages as read'
      };
    }
  },

  /**
   * Mark chat messages as read (legacy method - keeping for backward compatibility)
   * @param {string} conversationId - Conversation ID
   * @returns {Promise<Object>} - Mark as read response
   */
  markChatMessagesAsRead: async (conversationId) => {
    try {
      console.log('🔍 Marking messages as read for conversation:', conversationId);

      const response = await apiPost('/api/admin/chats/mark-read', {
        conversation_id: conversationId
      });

      console.log('✅ Mark as read Response:', response);
      return response;
    } catch (error) {
      console.error('Error marking messages as read:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to mark messages as read'
      };
    }
  }

};

/**
 * Update partnership status
 * @param {string} partnershipId - ID of the partnership
 * @param {Object} statusData - Status update data
 * @returns {Promise<Object>} - Updated partnership status
 */
userService.updatePartnershipStatus = async (partnershipId, statusData) => {
  try {
    // Fetch existing partnership details to validate
    const partnershipCheck = await userService.getBuilderPartnerships();
    const existingPartnerships = partnershipCheck.data || [];
    const matchingPartnership = existingPartnerships.find(p => p.id === partnershipId);

    if (!matchingPartnership) {
      console.error('Partnership not found in existing partnerships', {
        partnershipId,
        availablePartnerships: existingPartnerships.map(p => p.id)
      });
    }

    // Normalize status data with maximum field flexibility
    const payload = {
      // Core status fields
      status: statusData.status,
      current_status: statusData.status,
      partnership_status: statusData.status,

      // Comment/notes fields
      comment: statusData.comment || '',
      comments: statusData.comment || '',
      notes: statusData.comment || '',

      // Identification fields
      partnership_id: partnershipId,
      id: partnershipId,

      // Additional context
      updated_by: localStorage.getItem('userId') || 'unknown_user',
      source: 'web_app',
      timestamp: new Date().toISOString()
    };

    console.group('Partnership Status Update Investigation');
    console.log('Partnership Verification:', {
      matchingPartnership,
      existingPartnershipsCount: existingPartnerships.length
    });
    console.log('Raw Partnership ID:', partnershipId);
    console.log('Raw Status Data:', statusData);
    console.log('Normalized Payload:', payload);

    // Potential endpoint strategies
    const baseEndpoints = [
      // Exact match strategies
      `partnerships/${partnershipId}/status`,
      `crm/partnerships/${partnershipId}/status`,
      `builder/partnerships/${partnershipId}/status`,

      // Generic endpoints
      `partnerships/status`,
      `partnerships/update-status`,
      `partnership/status/update`,

      // Alternative routing
      `status/partnership/${partnershipId}`,
      `update/partnership/status`
    ];

    const prefixes = [
      'api/',
      '',
      '/api/',
      'v1/',
      '/v1/',
      'agent/'
    ];

    const endpoints = prefixes.flatMap(prefix =>
      baseEndpoints.map(endpoint => prefix + endpoint)
    );

    // Add absolute URLs as final fallback
    endpoints.push(
      `https://sandbox.rebax.in/api/partnerships/${partnershipId}/status`,
      `https://sandbox.rebax.in/api/crm/partnerships/${partnershipId}/status`
    );

    let lastError = null;
    for (const endpoint of endpoints) {
      try {
        console.log(`🔍 Attempting Endpoint: ${endpoint}`);
        console.log('Full Investigation URL:', `https://sandbox.rebax.in/${endpoint}`);

        const response = await apiPut(endpoint, payload, {
          headers: {
            'X-Partnership-Status': 'Update',
            'X-Request-Source': 'Agent',
            'X-Operation': 'Status_Change'
          }
        });

        console.log('✅ Successful Response:', response);
        console.groupEnd();

        return {
          success: true,
          data: response.data,
          message: 'Partnership status updated successfully',
          endpoint: endpoint
        };
      } catch (err) {
        console.warn(`❌ Endpoint ${endpoint} Failed:`, err.message);
        console.log('Detailed Error:', {
          name: err.name,
          message: err.message,
          status: err.status,
          response: err.response
        });
        lastError = {
          ...err,
          failedEndpoint: endpoint
        };
      }
    }

    console.error('🚨 Exhaustive Endpoint Attempt Failed:', lastError);
    console.groupEnd();

    // Enhanced error reporting
    return {
      success: false,
      message: lastError?.response?.data?.message ||
               lastError?.message ||
               'Exhaustive status update attempts failed',
      error: {
        details: lastError,
        attemptedEndpoints: endpoints
      }
    };
  } catch (error) {
    console.error('🔥 Critical Partnership Status Update Error', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Catastrophic status update failure',
      error: error
    };
  }
};
userService.getReviews = async (filters = {}) => {
  try {
    const response = await apiGet('/api/auth/reviews', {
      params: filters
    });

    const reviews =
      response.data?.reviews ||
      response.data?.data?.reviews ||
      response.data ||
      [];

    const total =
      response.data?.total ||
      response.data?.data?.total ||
      reviews.length ||
      0;

    return {
      success: true,
      data: {
        reviews,
        total
      }
    };
  } catch (error) {
    console.error('Error fetching reviews:', error);
    return {
      success: false,
      data: {
        reviews: [],
        total: 0
      },
      message: error.response?.data?.message || 'Failed to fetch reviews'
    };
  }
};
// Add missing partnership methods from mobile app

/**
 * Get partnerships for builder (same as getBuilderPartnerships but with different name)
 */
userService.getPartnerships = async () => {
  return await userService.getBuilderPartnerships();
};

/**
 * Get agent registration requests
 */
userService.getAgentRegistrationRequests = async () => {
  try {
    const response = await apiGet('/api/auth/builder/agent-registrations');
    return {
      success: true,
      data: response.data || []
    };
  } catch (error) {
    console.error('Error fetching agent registration requests:', error);
    return {
      success: false,
      data: [],
      message: error.response?.data?.message || 'Failed to fetch agent registration requests'
    };
  }
};

/**
 * Update agent registration status
 */
userService.updateAgentRegistrationStatus = async (requestId, statusData) => {
  try {
    const response = await apiPut(`/api/auth/builder/agent-registrations/${requestId}`, statusData);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error updating agent registration status:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to update agent registration status'
    };
  }
};

/**
 * Update partner status
 */
userService.updatePartnerStatus = async (partnershipId, statusData) => {
  try {
    const response = await apiPut(`/api/auth/builder/partnership-status/${partnershipId}`, statusData);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error updating partner status:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to update partner status'
    };
  }
};

/**
 * Get partnership history
 */
userService.getBuilderPartnershipHistory = async (partnershipId) => {
  try {
    const response = await apiGet(`/api/auth/builder/partnership-history/${partnershipId}`);
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching partnership history:', error);
    return {
      success: false,
      data: null,
      message: error.response?.data?.message || 'Failed to fetch partnership history'
    };
  }
};

/**
 * Get team users
 */
userService.getTeamUsers = async () => {
  try {
    const response = await apiGet('/api/users/team');
    return {
      success: true,
      data: response.data || []
    };
  } catch (error) {
    console.error('Error fetching team users:', error);
    return {
      success: false,
      data: [],
      message: error.response?.data?.message || 'Failed to fetch team users'
    };
  }
};

/**
 * Get builder registration settings (auto-accept/reject rules)
 */
userService.getBuilderRegistrationSettings = async () => {
  try {
    const response = await apiGet('/api/auth/builder/registration-settings');
    return {
      success: true,
      data: response.data || response
    };
  } catch (error) {
    console.error('Error getting builder registration settings:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to get registration settings'
    };
  }
};

/**
 * Save builder registration settings (auto-accept/reject rules)
 */
userService.saveBuilderRegistrationSettings = async (settings) => {
  try {
    const response = await apiPut('/api/auth/builder/registration-settings', settings);
    return {
      success: true,
      data: response.data || response,
      message: 'Registration settings saved successfully'
    };
  } catch (error) {
    console.error('Error saving builder registration settings:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to save registration settings'
    };
  }
};

/**
 * Get builder contacts (builders who accepted this agent)
 */
userService.getBuilderContacts = async () => {
  try {
    console.log('🔍 Fetching Builder Contacts');
    const response = await apiGet('/api/users/builder-contacts');
    console.log('Builder Contacts Response:', response);
    return {
      success: true,
      data: response.data || response
    };
  } catch (error) {
    console.error('Error fetching builder contacts:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to fetch builder contacts'
    };
  }
};

/**
 * Get call history for agent
 */
userService.getCallHistory = async () => {
  try {
    console.log('🔍 Fetching Call History');
    const response = await apiGet('/api/users/dialed-history');
    console.log('Call History Response:', response);
    return {
      success: true,
      data: response.data || response
    };
  } catch (error) {
    console.error('Error fetching call history:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to fetch call history'
    };
  }
};

/**
 * Update agent status for a builder
 */
userService.updateAgentStatus = async (builderId, agent_status, comment = '') => {
  try {
    console.log('🔍 Updating Agent Status:', { builderId, agent_status, comment });
    const response = await apiPut(`/api/users/builder-contacts/${builderId}/agent-status`, {
      agent_status: agent_status,
      comment: comment
    });
    console.log('Update Agent Status Response:', response);
    return {
      success: true,
      data: response.data || response,
      message: 'Agent status updated successfully'
    };
  } catch (error) {
    console.error('Error updating agent status:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to update agent status'
    };
  }
};

/**
 * Get builder activity history
 */
userService.getBuilderActivityHistory = async (builderId) => {
  try {
    console.log('🔍 Fetching Builder Activity History for:', builderId);
    const response = await apiGet(`/api/users/builder-contacts/${builderId}/activity-history`);
    console.log('Builder Activity Response:', response);
    return {
      success: true,
      data: response.data || response
    };
  } catch (error) {
    console.error('Error fetching builder activity history:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to fetch builder activity history'
    };
  }
};

/**
 * Get agent public information (for agent profile)
 */
userService.getAgentPublicInfo = async (agentId) => {
  try {
    console.log('🔍 Fetching Agent Public Info for:', agentId);
    const response = await apiGet(`/api/public/agent/${agentId}`);
    console.log('Agent Public Info Response:', response);
    return {
      success: true,
      data: response.data || response
    };
  } catch (error) {
    console.error('Error fetching agent public info:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to fetch agent public info'
    };
  }
};

/**
 * Get agency public information (for agency profile)
 */
userService.getAgencyPublicInfo = async (agencyId) => {
  try {
    console.log('🔍 Fetching Agency Public Info for:', agencyId);
    const response = await apiGet(`/api/public/agency/${agencyId}`);
    console.log('Agency Public Info Response:', response);
    return {
      success: true,
      data: response.data || response
    };
  } catch (error) {
    console.error('Error fetching agency public info:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to fetch agency public info'
    };
  }
};

/**
 * Get agent reviews by ID
 */
userService.getAgentReviewsById = async (agentId) => {
  try {
    console.log('🔍 Fetching Agent Reviews for:', agentId);
    const response = await apiGet(`/api/public/agent/${agentId}/reviews`);
    console.log('Agent Reviews Response:', response);
    return {
      success: true,
      data: response.data || response
    };
  } catch (error) {
    console.error('Error fetching agent reviews:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to fetch agent reviews'
    };
  }
};

/**
 * Add agent review
 */
userService.addAgentReview = async (agentId, reviewData) => {
  try {
    console.log('🔍 Adding Agent Review for:', agentId, reviewData);

    // Use the same API endpoint as mobile app
    const payload = {
      user_id: agentId,
      rating: reviewData.rating,
      review: reviewData.comment,
      review_type: reviewData.review_type,
    };

    console.log('Review payload:', payload);
    const response = await apiPost('/api/auth/reviews', payload);
    console.log('Add Agent Review Response:', response);
    return {
      success: true,
      data: response.data || response,
      message: 'Review added successfully'
    };
  } catch (error) {
    console.error('Error adding agent review:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to add review'
    };
  }
};

/**
 * Get agency reviews by ID
 */
userService.getAgencyReviewsById = async (agencyId) => {
  try {
    console.log('🔍 Fetching Agency Reviews for:', agencyId);
    const response = await apiGet(`/api/public/agency/${agencyId}/reviews`);
    console.log('Agency Reviews Response:', response);
    return {
      success: true,
      data: response.data || response
    };
  } catch (error) {
    console.error('Error fetching agency reviews:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to fetch agency reviews'
    };
  }
};

/**
 * Add agency review
 */
userService.addAgencyReview = async (agencyId, reviewData) => {
  try {
    console.log('🔍 Adding Agency Review for:', agencyId, reviewData);

    // Use the same API endpoint as mobile app but for agency
    const payload = {
      agency_id: agencyId,
      rating: reviewData.rating,
      comment: reviewData.comment,
      review_type: reviewData.review_type,
    };

    console.log('Agency review payload:', payload);
    const response = await apiPost('/api/auth/agency/reviews', payload);
    console.log('Add Agency Review Response:', response);
    return {
      success: true,
      data: response.data || response,
      message: 'Review added successfully'
    };
  } catch (error) {
    console.error('Error adding agency review:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to add review'
    };
  }
};

/**
 * Get sub-user lead count (matching mobile app functionality)
 * @param {string} userId - Sub-user ID
 * @returns {Promise<Object>} - { success, lead_count }
 */
userService.getSubUserLeadCount = async (userId) => {
  try {
    const response = await apiGet(`/api/users/sub-users/${userId}/lead-count`);
    return {
      success: true,
      lead_count: response?.data?.lead_count || response?.lead_count || 0
    };
  } catch (error) {
    console.error('Error fetching sub-user lead count:', error);
    return { success: true, lead_count: 0 };
  }
};

/**
 * Get all FAQs
 */
userService.getFAQs = async () => {
  try {
    console.log('🔍 Fetching FAQs');
    const response = await apiGet('/api/admin/faqs');
    console.log('FAQs Response:', response);
    return {
      success: true,
      data: response.data || response || []
    };
  } catch (error) {
    console.error('Error fetching FAQs:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to fetch FAQs',
      data: []
    };
  }
};

/**
 * Create a new FAQ
 */
userService.createFAQ = async (faqData) => {
  try {
    console.log('🔍 Creating FAQ:', faqData);
    const response = await apiPost('/api/admin/faqs', faqData);
    console.log('Create FAQ Response:', response);
    return {
      success: true,
      data: response.data || response,
      message: 'FAQ created successfully'
    };
  } catch (error) {
    console.error('Error creating FAQ:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to create FAQ'
    };
  }
};

/**
 * Update an existing FAQ
 */
userService.updateFAQ = async (faqId, faqData) => {
  try {
    console.log('🔍 Updating FAQ:', faqId, faqData);
    const response = await apiPut(`/api/admin/faqs/${faqId}`, faqData);
    console.log('Update FAQ Response:', response);
    return {
      success: true,
      data: response.data || response,
      message: 'FAQ updated successfully'
    };
  } catch (error) {
    console.error('Error updating FAQ:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to update FAQ'
    };
  }
};

/**
 * Delete an FAQ
 */
userService.deleteFAQ = async (faqId) => {
  try {
    console.log('🔍 Deleting FAQ:', faqId);
    const response = await apiDelete(`/api/admin/faqs/${faqId}`);
    console.log('Delete FAQ Response:', response);
    return {
      success: true,
      data: response.data || response,
      message: 'FAQ deleted successfully'
    };
  } catch (error) {
    console.error('Error deleting FAQ:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to delete FAQ'
    };
  }
};

/**
 * Get all admin users (matching React Native functionality)
 */
userService.getAllAdminUsers = async (filters = {}) => {
  try {
    console.log('🔍 Fetching admin users with filters:', filters);

    // Build query string from filters
    const queryParams = new URLSearchParams();
    if (filters.search) queryParams.append('search', filters.search);
    if (filters.role) queryParams.append('role', filters.role);

    const endpoint = `/api/admin/users${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await apiGet(endpoint);
    console.log('Admin Users Response:', response);

    return {
      success: true,
      data: {
        users: response.data?.users || response.users || response.data || []
      }
    };
  } catch (error) {
    console.error('Error fetching admin users:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to fetch admin users',
      data: { users: [] }
    };
  }
};

/**
 * Create a new admin user (matching React Native functionality)
 */
userService.createAdminUser = async (userData) => {
  try {
    console.log('🔍 Creating admin user:', userData);
    const response = await apiPost('/api/admin/users', userData);
    console.log('Create Admin User Response:', response);
    return {
      success: true,
      data: response.data || response,
      message: 'Admin user created successfully'
    };
  } catch (error) {
    console.error('Error creating admin user:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to create admin user'
    };
  }
};
userService.updateAdminUser = async (userId, userData) => {
  try {
    console.log('🔍 Updating admin user:', userData);

    const response = await apiPut(`/api/admin/users/${userId}`, userData);

    console.log('Update Admin User Response:', response);

    return {
      success: true,
      data: response.data || response,
      message: 'Admin user updated successfully'
    };

  } catch (error) {
    console.error('Error updating admin user:', error);

    return {
      success: false,
      message: error.response?.data?.message || 'Failed to update admin user'
    };
  }
};

/**
 * Delete an admin user.
 */
userService.deleteAdminUser = async (userId) => {
  try {
    console.log('🔍 Deleting admin user:', userId);
    const response = await apiDelete(`/api/admin/users/${userId}`);
    return {
      success: true,
      data: response.data || response,
      message: 'Admin user deleted successfully'
    };
  } catch (error) {
    console.error('Error deleting admin user:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to delete admin user'
    };
  }
};

userService.getAdminLegalTerms = async (userType) => {
  const params = userType ? { user_type: userType } : {};
  return await apiGet('/api/admin/legal-terms', params);
};

userService.saveLegalTerms = async (data) => {
  return await apiPost('/api/admin/legal-terms', data);
};

userService.deleteLegalTerms = async (id) => {
  return await apiDelete(`/api/admin/legal-terms/${id}`);
};

userService.convertDocxToHtml = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return await apiPost('/api/admin/convert-docx', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

userService.adminVerifyRera = async (userId, status, reason) => {
  return await apiPut('/api/auth/rera/verify', { user_id: userId, status, reason });
};

userService.adminVerifyGst = async (userId, status, reason) => {
  return await apiPut('/api/auth/gst/verify', { user_id: userId, status, reason });
};

export default userService;
